import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export const STICKY_COLORS = ["yellow", "pink", "blue", "green"] as const;
export type StickyColor = (typeof STICKY_COLORS)[number];

export interface StickyNote {
  id: string;
  title: string;
  content: string;
  sticky_section: string;
  sticky_x: number;
  sticky_y: number;
  sticky_color: StickyColor;
  updated_at: string;
  user_id: string;
}

const MAX_TITLE = 120;
const MAX_CONTENT = 2000;

export const clampStickyText = (value: string, max: number) => value.slice(0, max).trim();

export const useStickyNotes = (leadId: string, section: string) => {
  const queryClient = useQueryClient();
  const queryKey = ["sticky-notes", leadId, section];

  const { data: notes = [], isLoading } = useQuery({
    queryKey,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("notes")
        .select("id, title, content, sticky_section, sticky_x, sticky_y, sticky_color, updated_at, user_id")
        .eq("lead_id", leadId)
        .eq("sticky_section", section)
        .order("created_at", { ascending: true });
      if (error) throw error;
      return (data || []).map((n: any) => ({
        ...n,
        sticky_x: Number(n.sticky_x ?? 16),
        sticky_y: Number(n.sticky_y ?? 16),
        sticky_color: (STICKY_COLORS as readonly string[]).includes(n.sticky_color)
          ? n.sticky_color
          : "yellow",
      })) as StickyNote[];
    },
    enabled: Boolean(leadId && section),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey });

  const create = useMutation({
    mutationFn: async (input: { x: number; y: number; color: StickyColor }) => {
      const { data: auth } = await supabase.auth.getUser();
      if (!auth.user) throw new Error("You need to be signed in to add a note.");
      const { error } = await supabase.from("notes").insert({
        lead_id: leadId,
        user_id: auth.user.id,
        title: "Sticky note",
        content: "",
        sticky_section: section,
        sticky_x: input.x,
        sticky_y: input.y,
        sticky_color: input.color,
      } as any);
      if (error) throw error;
    },
    onSuccess: invalidate,
  });

  const updateContent = useMutation({
    mutationFn: async (input: { id: string; title: string; content: string }) => {
      const title = clampStickyText(input.title, MAX_TITLE) || "Sticky note";
      const content = clampStickyText(input.content, MAX_CONTENT);
      const { error } = await supabase
        .from("notes")
        .update({ title, content } as any)
        .eq("id", input.id);
      if (error) throw error;
    },
    onSuccess: invalidate,
  });

  const updatePosition = useMutation({
    mutationFn: async (input: { id: string; x: number; y: number }) => {
      const { error } = await supabase
        .from("notes")
        .update({ sticky_x: Math.round(input.x), sticky_y: Math.round(input.y) } as any)
        .eq("id", input.id);
      if (error) throw error;
    },
    onSuccess: invalidate,
  });

  const updateColor = useMutation({
    mutationFn: async (input: { id: string; color: StickyColor }) => {
      const { error } = await supabase
        .from("notes")
        .update({ sticky_color: input.color } as any)
        .eq("id", input.id);
      if (error) throw error;
    },
    onSuccess: invalidate,
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("notes").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: invalidate,
  });

  return { notes, isLoading, create, updateContent, updatePosition, updateColor, remove };
};
